/**
 * Version Service
 * Manages app versioning, cache-busting, and update detection
 */

// App version - increment this when making breaking changes
export const APP_VERSION = '1.0.0';
export const STORAGE_VERSION = 1; // Increment when storage schema changes
export const CACHE_VERSION = 1; // Increment to force cache invalidation

const VERSION_KEY = 'app_version';
const STORAGE_VERSION_KEY = 'storage_version';
const LAST_UPDATE_CHECK_KEY = 'last_update_check';

interface VersionInfo {
  appVersion: string;
  storageVersion: number;
  timestamp: number;
}

/**
 * Get the current stored version info
 */
export const getStoredVersionInfo = (): VersionInfo | null => {
  try {
    const raw = localStorage.getItem(VERSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as VersionInfo;
  } catch {
    return null;
  }
};

/**
 * Store the current version info
 */
export const storeVersionInfo = (): void => {
  try {
    const info: VersionInfo = {
      appVersion: APP_VERSION,
      storageVersion: STORAGE_VERSION,
      timestamp: Date.now()
    };
    localStorage.setItem(VERSION_KEY, JSON.stringify(info));
  } catch (e) {
    console.warn('Failed to store version info:', e);
  }
};

/**
 * Check if the app has been updated since last visit
 */
export const hasAppUpdated = (): boolean => {
  const stored = getStoredVersionInfo();
  if (!stored) return true; // First visit or cleared storage
  return stored.appVersion !== APP_VERSION;
};

/**
 * Check if storage schema has changed and needs migration
 */
export const needsStorageMigration = (): boolean => {
  const stored = getStoredVersionInfo();
  if (!stored) return false; // No old data to migrate
  return stored.storageVersion < STORAGE_VERSION;
};

/**
 * Clear all cached data when app updates
 */
export const clearCacheOnUpdate = (): void => {
  if (hasAppUpdated()) {
    console.log(`App updated from ${getStoredVersionInfo()?.appVersion || 'unknown'} to ${APP_VERSION}`);
    
    // Clear service worker caches if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Force reload of any cached resources
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update();
        });
      });
    }
    
    storeVersionInfo();
  }
};

/**
 * Initialize version tracking on app start
 */
export const initVersionTracking = (): { isNewVersion: boolean; needsMigration: boolean } => {
  const isNewVersion = hasAppUpdated();
  const needsMigration = needsStorageMigration();
  
  if (isNewVersion) {
    clearCacheOnUpdate();
  }
  
  return { isNewVersion, needsMigration };
};

/**
 * Get build info for debugging
 */
export const getBuildInfo = (): Record<string, unknown> => {
  return {
    appVersion: APP_VERSION,
    storageVersion: STORAGE_VERSION,
    cacheVersion: CACHE_VERSION,
    buildTime: import.meta.env.VITE_BUILD_TIME || 'development',
    commitHash: import.meta.env.VITE_COMMIT_HASH || 'local'
  };
};
